/** @jsxImportSource @emotion/react */
import { useCallback, useState } from "react";
import { css } from "@emotion/react";
import { useDispatch, useSelector } from "react-redux";
import { Row } from "../../../layout/flexbox";
import * as Buttons from "../../../forms/elements/buttons";
import ErrorView from "../../../forms/ErrorView";
import ConfirmationModal from "../../../forms/elements/ConfirmationModal";
import { IAppState } from "../../../core/redux/IAppState";
import { ACTION_TYPE } from "../../../core/redux/actionType";
import { getError, getLoading } from "../../../core/redux/api/module";
import { showSnackbarAction } from "../../../snackbar/snackbarAction";
import { saveLiftAccessRulesAction } from "../../saveLiftAccessRulesAction";
import { LiftAccessRuleSet } from "./models";
import { mapAccessRuleChangeRequest } from "../requests/accessRuleChangeRequestMapper";
import { getAllValidationErrors } from "../validation/validationUtils";
import { hasAnyDateRangeOverlap } from "../blackoutDateRange/blackoutDateRange";
import { getResource } from "../../../language/resources/getResource";
import { LiftAccessRuleSetEditorLayout } from "./LiftAccessRuleSetEditorLayout";
import { LiftAccessRuleSetHeader } from "./LiftAccessRuleSetHeader";
import saveIcon from "../../../assets/icons/disk.svg";
import { BoxedErrorInverse } from "../../../forms/elements/boxedMessage/boxedMessageStyles";
import { ROW_SPACING } from "../../../forms/styles";
import { useAccessRulesEditor } from "../useAccessRulesEditor";
import { LiftAccessRulesEditor } from "./LiftAccessRulesEditor";
import { createEmptyChangeSet } from "../requests/accessRuleRequestMapper";
import IosSpinner from "../../../forms/elements/loader/IosSpinner";
import deleteIcon from "../../../assets/icons/trashcan.svg";
import { buildSingleRuleSetDeleteRequest } from "../requests/singleRuleSetDeleteRequest";
import { bulkDeleteLiftAccessRuleSetsAction } from "../../bulkDeleteLiftAccessRuleSetsAction";
import { useApiActionHandler } from "../../../language/hooks/useApiActionHandler";

const ioSpinner = css`
  z-index: 100;
  position: fixed;
  top: 45vh;
  left: 50vw;
`;

const interactionBlocker = css`
  position: fixed;
  inset: 0;
  z-index: 9998;
  background: transparent;
  pointer-events: all;
`;

type Props = {
  ruleSet: LiftAccessRuleSet;
  onDismiss: () => void;
  onSave?: () => void;
  onDelete?: () => void;
};

export function LiftAccessRuleSetEditor({
  ruleSet,
  onDismiss,
  onSave,
  onDelete,
}: Props) {
  const dispatch = useDispatch();

  const resources = useSelector((s: IAppState) => s.locale);
  const dateFormat = useSelector(
    (s: IAppState) => s.resortToolsConfig.defaultDateFormat,
  );
  const userId = useSelector((s: IAppState) => s.userState.userId);
  const operatorId = useSelector((s: IAppState) => s.userState.userName);

  const isSaving = useSelector(
    (s: IAppState) =>
      !!getLoading(
        s,
        ACTION_TYPE.SAVE_LIFT_ACCESS_RULES_REQUEST,
        ACTION_TYPE.SAVE_LIFT_ACCESS_RULES_REQUEST,
      ),
  );

  const saveErrors = useSelector((s: IAppState) =>
    getError(
      s,
      ACTION_TYPE.SAVE_LIFT_ACCESS_RULES_REQUEST,
      ACTION_TYPE.SAVE_LIFT_ACCESS_RULES_REQUEST,
    ),
  );

  const isDeleting = useSelector(
    (s: IAppState) =>
      !!getLoading(
        s,
        ACTION_TYPE.BULK_DELETE_LIFT_ACCESS_RULES_REQUEST,
        ACTION_TYPE.BULK_DELETE_LIFT_ACCESS_RULES_REQUEST,
      ),
  );

  const deleteErrors = useSelector((s: IAppState) =>
    getError(
      s,
      ACTION_TYPE.BULK_DELETE_LIFT_ACCESS_RULES_REQUEST,
      ACTION_TYPE.BULK_DELETE_LIFT_ACCESS_RULES_REQUEST,
    ),
  );

  const showSnackbar = useCallback(
    (msg: string) => dispatch(showSnackbarAction(msg)),
    [dispatch],
  );

  const [initialRules] = useState({
    effectiveDateRangeRules: ruleSet.effectiveDateRangeRules,
    maxDaysRules: ruleSet.maxDaysRules,
    blackoutDateRangeRules: ruleSet.blackoutDateRangeRules,
    blackoutDayOfWeekRules: ruleSet.blackoutDayOfWeekRules,
  });

  const {
    editableRules,
    setEditableRules,
    changeSet,
    hasChanges,
    hasEmptyNonDeletedBlackoutDayOfWeekGroup,
    resetChanges,
  } = useAccessRulesEditor(initialRules);

  const [submitCount, setSubmitCount] = useState(0);
  const [hasChangesSinceSubmit, setHasChangesSinceSubmit] = useState(false);
  const [validationError, setValidationError] = useState<string>();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  useApiActionHandler({
    isPending: isSaving,
    error: saveErrors,

    onSuccess: () => {
      showSnackbar(getResource(resources, "ACCESS_RULE_EDIT_CHANGES_SAVED"));
      onSave?.();
      onDismiss();
    },

    onError: () => {
      setValidationError(getResource(resources, "ACCESS_RULE_SAVE_ERROR"));
    },
  });

  useApiActionHandler({
    isPending: isDeleting,
    error: deleteErrors,
    onSuccess: () => {
      onDelete?.();
      onDismiss();
    },
    onError: () => {
      setValidationError(getResource(resources, "ACCESS_RULE_DELETE_ERROR"));
    },
  });

  const onAnyEdit = () => {
    setHasChangesSinceSubmit(true);
    setValidationError(undefined);
  };

  const validateAndConfirm = () => {
    setSubmitCount((c) => c + 1);
    setHasChangesSinceSubmit(false);

    if (
      hasEmptyNonDeletedBlackoutDayOfWeekGroup ||
      hasAnyDateRangeOverlap(editableRules?.blackoutDateRangeRules ?? [])
    ) {
      setValidationError(
        getResource(
          resources,
          "ACCESS_RULE_EDIT_VALIDATION_ERROR_UNABLE_TO_SAVE",
        ),
      );
      return;
    }

    if (!hasChanges) {
      showSnackbar(getResource(resources, "ACCESS_RULE_EDIT_NO_CHANGES_FOUND"));
      onDismiss();
      return;
    }

    const validationErrors = getAllValidationErrors([
      changeSet?.effectiveDateRange.created ?? [],
      changeSet?.effectiveDateRange.updated ?? [],
      changeSet?.maxDays.created ?? [],
      changeSet?.maxDays.updated ?? [],
      changeSet?.blackoutDateRange.created ?? [],
      changeSet?.blackoutDateRange.updated ?? [],
      changeSet?.blackoutDayOfWeek.created ?? [],
      changeSet?.blackoutDayOfWeek.updated ?? [],
    ]);

    if (validationErrors.length > 0) {
      setValidationError(
        getResource(
          resources,
          "ACCESS_RULE_EDIT_VALIDATION_ERROR_UNABLE_TO_SAVE",
        ),
      );
      return;
    }

    setShowConfirmation(true);
  };

  const handleSave = () => {
    dispatch(
      saveLiftAccessRulesAction(
        mapAccessRuleChangeRequest({
          userId,
          operatorId,
          entitlementTypeId: ruleSet.entitlementTypeId,
          fulfillmentSeasonId: ruleSet.fulfillmentSeasonId,
          liftAccessRuleScopeCategoryId: ruleSet.scopeCategoryId,
          liftAccessRuleScopeEntityId: ruleSet.locationId,
          changeSet: changeSet ?? createEmptyChangeSet(),
        }),
      ),
    );

    setShowConfirmation(false);
  };

  const handleDeleteRuleSet = () => {
    if (!ruleSet) return;

    const deleteRequest = buildSingleRuleSetDeleteRequest({
      userId: userId,
      operatorId: operatorId,
      ruleSet,
    });

    dispatch(bulkDeleteLiftAccessRuleSetsAction(deleteRequest));
    setShowDeleteConfirmation(false);
  };

  return (
    <>
      {/* Block user interaction while saving */}
      {(isSaving || isDeleting) && <div css={interactionBlocker} />}

      {
        // Saving
        (isSaving || isDeleting) && <IosSpinner rootCss={ioSpinner} />
      }
      <LiftAccessRuleSetEditorLayout
        header={
          <LiftAccessRuleSetHeader ruleSet={ruleSet} resources={resources} />
        }
        rules={
          <LiftAccessRulesEditor
            rules={editableRules}
            sellingSeason={ruleSet.sellingSeasonDescription}
            resources={resources}
            dateFormat={dateFormat}
            submitCount={submitCount}
            hasChangesSinceSubmit={hasChangesSinceSubmit}
            onAnyEdit={onAnyEdit}
            onRulesChange={setEditableRules}
          />
        }
      />

      <ErrorView error={validationError} />

      <Row
        justifyContent="flex-end"
        rootCss={css`
          ${ROW_SPACING};
          margin-top: 10px;
        `}
      >
        <Buttons.BigSecondaryRed
          onClick={() => {
            setShowDeleteConfirmation(true);
          }}
        >
          {getResource(resources, "ACCESS_RULE_DELETE")}
        </Buttons.BigSecondaryRed>

        <Buttons.BigSecondary
          onClick={() => {
            resetChanges();
            onDismiss();
          }}
        >
          {getResource(resources, "ACCESS_RULE_MODAL_CANCEL")}
        </Buttons.BigSecondary>

        <Buttons.BigNeutral onClick={validateAndConfirm}>
          {getResource(resources, "ACCESS_RULE_MODAL_SAVE")}
        </Buttons.BigNeutral>
      </Row>

      {showConfirmation && (
        <ConfirmationModal
          resources={resources}
          icon={saveIcon}
          style={BoxedErrorInverse}
          title={getResource(resources, "ACCESS_RULE_MODAL_SAVE")}
          styledMessage={getResource(
            resources,
            "ACCESS_RULE_DATA_CHANGE_WARNING",
          )}
          message={getResource(resources, "ACCESS_RULE_EDIT_SAVE_CONFIRMATION")}
          positiveButtonText={getResource(resources, "ACCESS_RULE_MODAL_SAVE")}
          negativeButtonText={getResource(
            resources,
            "ACCESS_RULE_MODAL_CANCEL",
          )}
          onPositiveButtonClick={handleSave}
          onNegativeButtonClick={() => setShowConfirmation(false)}
          onDismiss={() => setShowConfirmation(false)}
        />
      )}

      {showDeleteConfirmation && (
        <ConfirmationModal
          resources={resources}
          icon={deleteIcon}
          style={BoxedErrorInverse}
          title={getResource(resources, "ACCESS_RULE_DELETE_ACCESS_RULES")}
          styledMessage={getResource(
            resources,
            "ACCESS_RULE_DATA_CHANGE_WARNING",
          )}
          message={getResource(resources, "ACCESS_RULE_DELETE_CONFIRMATION")}
          positiveButtonText={getResource(resources, "ACCESS_RULE_DELETE")}
          negativeButtonText={getResource(
            resources,
            "ACCESS_RULE_MODAL_CANCEL",
          )}
          onPositiveButtonClick={handleDeleteRuleSet}
          onNegativeButtonClick={() => setShowDeleteConfirmation(false)}
          onDismiss={() => setShowDeleteConfirmation(false)}
        />
      )}
    </>
  );
}
